import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  DeviceApiService,
  DevicePlatformName,
  DeviceRegistrationDto,
  PUSH_DEVICE_ID_STORAGE_KEY,
  PUSH_TOKEN_STORAGE_KEY,
} from '@core/api/device-api.service';
import { notificationActions } from '@core/config/notification-routing.config';
import { AppNotificationsService } from '@core/services/app-notifications.service';
import { AuthService } from '@core/services/auth.service';
import { ChatViewingService } from '@core/services/chat-viewing.service';
import { WorkspaceRouteService } from '@core/services/workspace-route.service';
import { WorkspaceService } from '@core/services/workspace.service';
import { readPushField, unwrapPushPayload } from '@core/utils/push-payload.util';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

/** Permission outcome, normalised across the native and web APIs. */
export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * Registers this install for out-of-app notifications and routes taps.
 *
 * Two transports sit behind one API:
 *
 * - **Native (Android/iOS)** — `@capacitor/push-notifications` obtains an FCM
 *   token. The OS draws the tray/lock-screen notification itself while the app
 *   is backgrounded or killed, which is the case SignalR cannot serve.
 * - **Browser / Electron** — a service worker subscribes to Web Push with the
 *   deployment's VAPID key and draws the notification from `push-sw.js`.
 *
 * In-app delivery is unchanged: {@link AppNotificationsService} still shows
 * notifications over SignalR while a window is open, and this service
 * deliberately suppresses the OS notification in that case so nothing doubles.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  private readonly deviceApi = inject(DeviceApiService);
  private readonly appNotifications = inject(AppNotificationsService);
  private readonly auth = inject(AuthService);
  private readonly chatViewing = inject(ChatViewingService);
  private readonly workspace = inject(WorkspaceService);
  private readonly workspaceRoute = inject(WorkspaceRouteService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  private started = false;
  private routingReady = false;
  private pendingTap: Record<string, string> | null = null;
  private tapListenerBound = false;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private nativeListenerHandles: Array<{ remove: () => Promise<void> }> = [];
  private readonly serviceWorkerMessageHandler = (event: MessageEvent): void =>
    this.onServiceWorkerMessage(event);

  /** Current permission state, for the notification-settings toggle. */
  readonly permission = signal<PushPermission>('prompt');
  /** Whether this install currently has a live registration. */
  readonly registered = signal(false);

  /**
   * Binds the native tray-tap listener before the workspace shell exists, so a
   * cold-start tap is queued instead of dropped. Safe to call more than once.
   */
  async listenForTaps(): Promise<void> {
    if (!Capacitor.isNativePlatform() || this.tapListenerBound) return;
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await this.bindTapListener(PushNotifications);
    } catch (err) {
      console.warn('Could not listen for notification taps', err);
    }
  }

  /**
   * The workspace shell is mounted and guards have passed — flush a tap that
   * arrived while the app was still booting.
   */
  markRoutingReady(): void {
    this.routingReady = true;
    void this.flushPendingTap();
  }

  /**
   * Sets up push for the signed-in user. Safe to call on every shell mount —
   * re-registering refreshes the existing row rather than creating a new one.
   */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    try {
      if (Capacitor.isNativePlatform()) {
        await this.startNative();
      } else {
        await this.startWeb();
      }
    } catch (err) {
      // Push is an enhancement over the in-app inbox. If it cannot be set up —
      // permission denied, no VAPID key, an unsupported browser — the app must
      // carry on working exactly as before. Clear the guard so a later shell
      // mount (or a grant from system settings) can try again.
      this.started = false;
      console.warn('Push notifications unavailable', err);
    }
  }

  /**
   * Removes this device's registration. Called on sign-out so notifications for
   * the previous user stop arriving on a shared device.
   */
  async stop(): Promise<void> {
    const token = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    if (token) {
      try {
        await firstValueFrom(this.deviceApi.unregister(token));
      } catch {
        // The row also retires itself once delivery starts failing, so a failed
        // unregister is not worth blocking sign-out over.
      }
      localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
    }

    // A browser keeps one Web Push endpoint per service worker, so the next
    // account to sign in here would inherit this one. Dropping it forces a
    // fresh endpoint and stops the previous user's notifications from being
    // delivered to whoever is signed in next.
    await this.dropWebSubscription();

    await this.removeNativeListeners();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener(
        'message',
        this.serviceWorkerMessageHandler,
      );
    }

    this.registered.set(false);
    this.started = false;
    this.routingReady = false;
  }

  // ================= Native (Android / iOS) =================

  private async startNative(): Promise<void> {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // A root service survives logout/login navigation. Clear listeners from the
    // previous authenticated shell before binding callbacks for the new user.
    await this.removeNativeListeners();

    let status = await PushNotifications.checkPermissions();
    if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
      status = await PushNotifications.requestPermissions();
    }

    if (status.receive !== 'granted') {
      this.permission.set('denied');
      this.started = false;
      return;
    }
    this.permission.set('granted');

    if (Capacitor.getPlatform() === 'android') {
      await this.ensureAndroidChannel(PushNotifications);
    }

    // The token arrives asynchronously on the `registration` event rather than
    // as a return value, so listeners must be attached before registering.
    this.nativeListenerHandles.push(await PushNotifications.addListener('registration', (token) => {
      void this.sendRegistration({
        platform: this.nativePlatformName(),
        token: token.value,
      });
    }));

    this.nativeListenerHandles.push(await PushNotifications.addListener('registrationError', (err) => {
      console.warn('Push registration failed', err);
    }));

    // Fired only while the app is in the foreground; the OS handles the
    // background case on its own. Swallow chat pushes the user is already
    // looking at (or that they sent), and refresh the inbox for everything else.
    this.nativeListenerHandles.push(
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        void this.onForegroundPush(PushNotifications, notification);
      }),
    );

    await this.bindTapListener(PushNotifications);

    await PushNotifications.register();
  }

  /**
   * Kept off the registration-listener list so token refresh / logout does not
   * drop a cold-start tap that arrived before the shell finished booting.
   */
  private async bindTapListener(
    plugin: typeof import('@capacitor/push-notifications')['PushNotifications'],
  ): Promise<void> {
    if (this.tapListenerBound) return;
    this.tapListenerBound = true;
    await plugin.addListener('pushNotificationActionPerformed', (action) => {
      void this.handleNotificationTap(action.notification);
    });
  }

  /**
   * Foreground FCM still reaches JS. Drop it when it is our own send or the
   * thread already on screen, and pull down any tray entry Capacitor posted.
   */
  private async onForegroundPush(
    plugin: typeof import('@capacitor/push-notifications')['PushNotifications'],
    notification: { id?: string; tag?: string; data?: unknown },
  ): Promise<void> {
    const data = unwrapPushPayload(notification);
    if (this.shouldSilenceChatPush(data)) {
      await this.dismissForegroundPush(plugin, notification, data);
      return;
    }
    void this.appNotifications.refresh();
  }

  /**
   * Clears tray entries for a thread the user has just opened. Messages that
   * arrived while the app was backgrounded are still in the shade after they
   * have been read, which is exactly what a chat app is expected to tidy up.
   */
  async clearConversationTray(conversationId: number): Promise<void> {
    if (!Capacitor.isNativePlatform() || conversationId <= 0) return;
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await this.removeConversationEntries(PushNotifications, String(conversationId));
    } catch {
      // Best-effort tidying; the thread is on screen either way.
    }
  }

  private shouldSilenceChatPush(data: Record<string, string>): boolean {
    const sender = readPushField(data, 'senderUserGuid');
    const me = this.auth.currentUser()?.id;
    if (sender && me && sender.toLowerCase() === me.toLowerCase()) {
      return true;
    }

    const conversationId = Number(readPushField(data, 'conversationId'));
    return Number.isFinite(conversationId) && this.chatViewing.isViewing(conversationId);
  }

  private async dismissForegroundPush(
    plugin: typeof import('@capacitor/push-notifications')['PushNotifications'],
    notification: { id?: string; tag?: string },
    data: Record<string, string>,
  ): Promise<void> {
    try {
      const conversationId = readPushField(data, 'conversationId');
      const removed = await this.removeConversationEntries(
        plugin,
        conversationId,
        notification.id,
      );
      if (!removed && notification.id != null && notification.id !== '') {
        await plugin.removeDeliveredNotifications({
          notifications: [{ id: String(notification.id), data: {} }],
        });
      }
    } catch {
      // Best-effort: the thread already shows the message over SignalR.
    }
  }

  /**
   * Removes every delivered entry belonging to one conversation. Matching is
   * deliberately broad — on Android the foreground event carries the FCM
   * message id rather than the OS notification id, so the conversation tag and
   * the payload are the reliable handles.
   */
  private async removeConversationEntries(
    plugin: typeof import('@capacitor/push-notifications')['PushNotifications'],
    conversationId: string,
    notificationId?: string,
  ): Promise<boolean> {
    const conversationTag = conversationId ? `conversation_${conversationId}` : '';
    const delivered = await plugin.getDeliveredNotifications();
    const toRemove = (delivered.notifications ?? []).filter((item) => {
      if (notificationId != null && String(item.id) === String(notificationId)) {
        return true;
      }
      if (conversationTag && item.tag === conversationTag) {
        return true;
      }
      const payload = unwrapPushPayload(item);
      return (
        conversationId !== '' &&
        readPushField(payload, 'conversationId') === conversationId
      );
    });

    if (toRemove.length === 0) return false;
    await plugin.removeDeliveredNotifications({ notifications: toRemove });
    return true;
  }

  /**
   * Android 8+ posts every notification to a channel and silently discards
   * anything aimed at one that does not exist. The id must match the
   * `Push:Fcm:ChannelId` the backend sends and the manifest default.
   */
  private async ensureAndroidChannel(
    plugin: typeof import('@capacitor/push-notifications')['PushNotifications'],
  ): Promise<void> {
    try {
      await plugin.createChannel({
        id: 'quiziva_default',
        name: 'Quiziva',
        description: 'Assessment, class and account notifications.',
        // High importance is what lets the notification appear as a heads-up
        // banner and make a sound rather than sitting silently in the shade.
        importance: 5,
        visibility: 1,
        vibration: true,
        lights: true,
      });
      await plugin.createChannel({
        id: 'quiziva_messages',
        name: 'Messages',
        description: 'Chat messages.',
        importance: 5,
        visibility: 1,
        vibration: true,
        lights: true,
      });
    } catch (err) {
      // Channel creation is not supported on every OEM build; the manifest
      // default still applies, so this is not worth failing registration over.
      console.warn('Could not create the notification channel', err);
    }
  }

  private nativePlatformName(): DevicePlatformName {
    return Capacitor.getPlatform() === 'ios' ? 'Ios' : 'Android';
  }

  // ================= Web / Electron =================

  private async startWeb(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      this.permission.set('unsupported');
      return;
    }

    // Ask the backend first: on a deployment without VAPID keys there is nothing
    // to subscribe to, and prompting for permission we cannot use is hostile.
    const config = await firstValueFrom(this.deviceApi.webPushConfig());
    if (!config.enabled || !config.publicKey) {
      this.permission.set('unsupported');
      return;
    }

    this.swRegistration = await navigator.serviceWorker.register('/push-sw.js');
    navigator.serviceWorker.removeEventListener(
      'message',
      this.serviceWorkerMessageHandler,
    );
    navigator.serviceWorker.addEventListener(
      'message',
      this.serviceWorkerMessageHandler,
    );

    const permission = await Notification.requestPermission();
    this.permission.set(permission === 'granted' ? 'granted' : 'denied');
    if (permission !== 'granted') return;

    try {
      await this.subscribeAndRegister(config.publicKey, false);
    } catch (err) {
      // 409 means this browser's endpoint is still owned by an account that did
      // not sign out cleanly. Mint a fresh one rather than leaving this user
      // unregistered — and, worse, the previous owner's pushes arriving here.
      if (!isConflict(err)) throw err;
      await this.subscribeAndRegister(config.publicKey, true);
    }
  }

  /**
   * Subscribes this browser to Web Push and stores the registration.
   * `forceNew` discards any endpoint already held by the service worker.
   */
  private async subscribeAndRegister(publicKey: string, forceNew: boolean): Promise<void> {
    if (!this.swRegistration) return;

    if (forceNew) {
      await this.dropWebSubscription();
    }

    // Reuse an existing subscription when there is one: re-subscribing would
    // mint a new endpoint and orphan the row already stored for this browser.
    const existing = forceNew
      ? null
      : await this.swRegistration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await this.swRegistration.pushManager.subscribe({
        // Required by Chrome: every message must be shown to the user.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const json = subscription.toJSON();
    await this.sendRegistration({
      platform: this.webPlatformName(),
      token: subscription.endpoint,
      p256dh: json.keys?.['p256dh'] ?? null,
      auth: json.keys?.['auth'] ?? null,
    });
  }

  /** Cancels this browser's Web Push endpoint, ignoring an absent one. */
  private async dropWebSubscription(): Promise<void> {
    try {
      const registration =
        this.swRegistration ??
        (('serviceWorker' in navigator)
          ? await navigator.serviceWorker.getRegistration('/push-sw.js')
          : null);
      const subscription = await registration?.pushManager.getSubscription();
      await subscription?.unsubscribe();
    } catch {
      // Best-effort: the backend row has already been unregistered, and a stale
      // endpoint is reclaimed by install id on the next registration.
    }
  }

  /**
   * Electron reports itself as a normal web platform, so the desktop build is
   * told apart by its user agent. It only changes how the device is labelled.
   */
  private webPlatformName(): DevicePlatformName {
    return /electron/i.test(navigator.userAgent) ? 'Desktop' : 'Web';
  }

  private onServiceWorkerMessage(event: MessageEvent): void {
    const message = event.data as {
      type?: string;
      data?: Record<string, string>;
      payload?: unknown;
    } | null;
    if (!message) return;

    if (message.type === 'push') {
      const data = unwrapPushPayload(message.payload ?? message.data ?? message);
      if (this.shouldSilenceChatPush(data)) return;
      void this.appNotifications.refresh();
      return;
    }

    if (message.type === 'notification-click') {
      void this.handleNotificationTap(message.data ?? {});
    }
  }

  // ================= Shared =================

  private async sendRegistration(
    partial: Pick<DeviceRegistrationDto, 'platform' | 'token' | 'p256dh' | 'auth'>,
  ): Promise<void> {
    const dto: DeviceRegistrationDto = {
      ...partial,
      deviceId: this.deviceId(),
      deviceName: describeDevice(),
      appVersion: null,
      language: this.translate.getCurrentLang() ?? 'en',
    };

    await firstValueFrom(this.deviceApi.register(dto));
    localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, partial.token);
    this.registered.set(true);
  }

  private async removeNativeListeners(): Promise<void> {
    const handles = this.nativeListenerHandles.splice(0);
    await Promise.all(handles.map(async (handle) => {
      try {
        await handle.remove();
      } catch {
        // Native teardown is best-effort; stale callbacks are harmless once the
        // backend token has been unregistered, and logout must keep moving.
      }
    }));
  }

  /**
   * A stable id for this install, so a token rotation replaces the previous
   * registration instead of leaving an undeliverable row behind.
   */
  private deviceId(): string {
    let id = localStorage.getItem(PUSH_DEVICE_ID_STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(PUSH_DEVICE_ID_STORAGE_KEY, id);
    }
    return id;
  }

  /**
   * Opens what the notification refers to. Routing reuses the same title-matching
   * rules the in-app inbox uses, so a tap and a click land in the same place.
   */
  private async handleNotificationTap(raw: unknown): Promise<void> {
    const data = unwrapPushPayload(raw);
    if (!this.routingReady) {
      this.pendingTap = data;
      return;
    }
    await this.routeFromPushData(data);
  }

  private async flushPendingTap(): Promise<void> {
    const data = this.pendingTap;
    if (!data || !this.routingReady) return;
    this.pendingTap = null;
    await this.routeFromPushData(data);
  }

  private async routeFromPushData(data: Record<string, string>): Promise<void> {
    void this.appNotifications.refresh();

    // The notification may belong to a workspace other than the active one;
    // routing before switching would land on the right page in the wrong scope.
    // A workspace the user has since lost access to simply is not found, and the
    // tap then resolves in whatever workspace is already active.
    const organizationId = readPushField(data, 'organizationId');
    const currentId = this.workspace.currentWorkspace()?.id ?? '';
    if (organizationId && organizationId !== String(currentId)) {
      this.workspace.selectById(organizationId);
    }

    const conversationId = readPushField(data, 'conversationId', 'conversation_id');
    if (conversationId) {
      await this.router.navigate(['/messages'], {
        queryParams: { conversation: conversationId },
      });
      return;
    }

    if (readPushField(data, 'kind') === 'chat') {
      await this.router.navigate(['/messages']);
      return;
    }

    const title = readPushField(data, 'title');
    const [action] = notificationActions(title, this.workspace.currentType());
    if (!action) {
      await this.router.navigateByUrl('/notifications');
      return;
    }

    const url = action.absolute
      ? action.segments.join('/')
      : this.workspaceRoute.path(...action.segments);

    await this.router.navigateByUrl(url);
  }
}

/** True for the 409 the backend returns when an endpoint has another owner. */
function isConflict(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 409;
}

/**
 * Converts the URL-safe base64 VAPID key the backend serves into the
 * `Uint8Array` the Push API demands.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);

  // Backed by an explicit ArrayBuffer: `new Uint8Array(length)` widens to
  // ArrayBufferLike under TS 5.9, which BufferSource no longer accepts.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/** A short label so a user can recognise the device in security settings. */
function describeDevice(): string {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'iOS device';
  if (platform === 'android') return 'Android device';
  if (/electron/i.test(navigator.userAgent)) return 'Desktop app';

  const ua = navigator.userAgent;
  if (/edg/i.test(ua)) return 'Edge browser';
  if (/chrome/i.test(ua)) return 'Chrome browser';
  if (/firefox/i.test(ua)) return 'Firefox browser';
  if (/safari/i.test(ua)) return 'Safari browser';
  return 'Browser';
}
